import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Search,
  Plus,
  Loader2,
  AlertTriangle,
  Eye,
  Clock,
  CheckCircle,
  CreditCard,
  Send,
  Check,
  Package,
  Truck,
  PackageCheck,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  MoreHorizontal,
  Flag,
  XCircle,
  X,
  Pencil,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import PrescriptionFormSheet from '@/components/clinic/PrescriptionFormSheet';
import PrescriptionDetailSheet from '@/components/clinic/PrescriptionDetailSheet';
import PaymentConfirmationDialog from '@/components/ui/payment-confirmation-dialog';
import VerificationConfirmationDialog from '@/components/dialog/verification-confirmation-dialog';
import { toast } from '@/hooks/use-toast';
import { getDefaultContactValue } from '@/lib/utils';
import { PaginationParams, Prescription } from '@/lib/types';
import { prescriptionService } from '@/services/prescriptionService';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SmartPagination from '../ui/smart-pagination';
import PaymentSheet from './PaymentSheet';
import { useClinic } from '@/contexts/ClinicContext';
import SendPaymentLinkDialog from '../dialog/SendPaymentLinkDialog';
import { formatToLocalDate } from '@/lib/utils';
import { permissionCheck } from '@/lib/permission-check';
import CancelPrescriptionDialog from './Prescriptions/CancelPrescriptionDialog';
import EditMedicationsDialog from './EditMedicationsDialog';

const ClinicPrescriptions = () => {
  const { user } = useAuth();
  const { clinic, isPractitioner, isAdmin, isClinicStaff, rolePermissions, isGuided } =
    useClinic();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status') || '';
  const [searchTerm, setSearchTerm] = useState('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [isPaymentConfirmationOpen, setIsPaymentConfirmationOpen] = useState(false);
  const [isVerificationConfirmationOpen, setIsVerificationConfirmationOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const [editingPrescription, setEditingPrescription] = useState<Prescription | null>(null);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isVerificationProcessing, setIsVerificationProcessing] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState(statusFromUrl);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    filter: {},
    sortBy: 'created_at',
    sortOrder: 'DESC',
    search: '',
    status: statusFromUrl,
  });
  const [isPaymentSheetOpen, setIsPaymentSheetOpen] = useState(false);
  const [isSendLinkSheetOpen, setIsSendLinkSheetOpen] = useState<{
    open: boolean;
    prescription: Prescription | null;
  }>({ open: false, prescription: null });
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isEditMedicationsOpen, setIsEditMedicationsOpen] = useState(false);
  const [isEditPrescriptionOpen, setIsEditPrescriptionOpen] = useState(false);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({
    all: 0,
    pending: 0,
    verified: 0,
    sent_to_pharmacy: 0,
    acknowledged: 0,
    filled: 0,
    shipped: 0,
    cancelled: 0,
    awaiting_payment: 0,
  });

  const handleNewPrescription = () => {
    setEditingPrescription(null);
    setIsSheetOpen(true);
  };

  const handleViewDetails = (prescription: Prescription) => {
    setSelectedPrescription(prescription);
    setIsDetailSheetOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className='h-3 w-3 text-slate-400' />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className='h-3 w-3 text-slate-700' />
    ) : (
      <ChevronDown className='h-3 w-3 text-slate-700' />
    );
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'verified':
        return 'Verified';
      case 'paid':
        return 'Paid';
      case 'sent_to_pharmacy':
        return 'Sent to Pharmacy';
      case 'acknowledged':
        return 'Acknowledged';
      case 'filled':
        return 'Being Filled';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready for Shipment';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-500 hover:bg-amber-500 text-white border-0';
      case 'verified':
        return 'bg-brand-teal hover:bg-brand-teal text-white border-0';
      case 'paid':
        return 'bg-emerald-600 hover:bg-emerald-600 text-white border-0';
      case 'sent_to_pharmacy':
        return 'bg-brand-copper hover:bg-brand-copper text-white border-0';
      case 'acknowledged':
        return 'bg-indigo-500 hover:bg-indigo-500 text-white border-0';
      case 'filled':
        return 'bg-orange-500 hover:bg-orange-500 text-white border-0';
      case 'shipped':
        return 'bg-sky-500 hover:bg-sky-500 text-white border-0';
      case 'delivered':
        return 'bg-emerald-600 hover:bg-emerald-600 text-white border-0';
      case 'ready':
        return 'bg-cyan-500 hover:bg-cyan-500 text-white border-0';
      case 'cancelled':
        return 'bg-red-500 hover:bg-red-500 text-white border-0';
      default:
        return 'bg-slate-500 hover:bg-slate-500 text-white border-0';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className='h-3 w-3' />;
      case 'verified':
        return <CheckCircle className='h-3 w-3' />;
      case 'paid':
        return <CreditCard className='h-3 w-3' />;
      case 'sent_to_pharmacy':
        return <Send className='h-3 w-3' />;
      case 'acknowledged':
        return <Check className='h-3 w-3' />;
      case 'filled':
        return <Package className='h-3 w-3' />;
      case 'shipped':
        return <Truck className='h-3 w-3' />;
      case 'delivered':
        return <PackageCheck className='h-3 w-3' />;
      case 'ready':
        return <PackageCheck className='h-3 w-3' />;
      case 'cancelled':
        return <XCircle className='h-3 w-3' />;
      default:
        return <AlertCircle className='h-3 w-3' />;
    }
  };

  const getStatusFilterLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'verified':
        return 'Verified';
      case 'sent_to_pharmacy':
        return 'Sent to Pharmacy';
      case 'acknowledged':
        return 'Acknowledged';
      case 'filled':
        return 'Being Filled';
      case 'shipped':
        return 'Shipped';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getPriorityText = (priority: number) => {
    switch (priority) {
      case -1:
        return 'Low';
      case 1:
        return 'Normal';
      case 2:
        return 'Elevated';
      case 3:
        return 'Urgent';
      default:
        return 'Normal';
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case -1:
        return 'bg-slate-100 text-slate-600 border-slate-200';
      case 1:
        return 'bg-green-100 text-green-700 border-green-200';
      case 2:
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 3:
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  const handleStatusUpdate = (prescriptionId: string, newStatus: Prescription['status']) => {
    setPrescriptions(prev =>
      prev.map(p => (p.id === prescriptionId ? { ...p, status: newStatus } : p))
    );
    toast({
      title: 'Status Updated',
      description: `Order status changed to ${getStatusText(newStatus)}`,
    });
  };

  const fetchStatusCounts = async () => {
    try {
      if (clinic?.id) {
        const response = await prescriptionService.getStatusCounts(clinic.id);
        if (response.data?.counts) {
          setStatusCounts(response.data.counts);
        }
      }
    } catch (error) {
      console.error('Error fetching status counts:', error);
    }
  };

  const fetchPrescriptions = async () => {
    try {
      setIsLoading(true);
      setPrescriptions([]);

      if (isAdmin || isPractitioner || isClinicStaff) {
        const response = await prescriptionService.getPrescriptionByClinicId(
          clinic?.id || '',
          {
            page: pagination.page,
            limit: 10,
            search: pagination.search,
            sortBy: sortField,
            sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
            status: statusFilter || '',
            payment_status: paymentStatusFilter === 'all' ? '' : paymentStatusFilter,
            // For practitioners, filter to show only prescriptions assigned to them
            ...(isPractitioner && !isAdmin && !isClinicStaff && {
              practitionerId: user?.practitionerId,
            }),
          }
        );
        setPrescriptions(response.data.prescriptions);
        setPagination(prev => ({ ...prev, ...response.data.pagination }));
      } else {
        const response = await prescriptionService.getPrescriptionByPractitionerID(
          user?.practitionerId || '',
          {
            page: pagination.page,
            limit: 10,
            search: pagination.search,
            sortBy: sortField,
            sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
            status: statusFilter || '',
            payment_status: paymentStatusFilter === 'all' ? '' : paymentStatusFilter,
          }
        );
        setPrescriptions(response.data.prescriptions);
        setPagination(prev => ({ ...prev, ...response.data.pagination }));
      }
      
      // Refresh status counts
      fetchStatusCounts();
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreationSuccess = () => {
    fetchPrescriptions();
  };

  // Debounced search
  useEffect(() => {
    const debouncedSearch = setTimeout(() => {
      setPagination(prev => ({
        ...prev,
        page: 1,
        search: searchTerm,
      }));
    }, 500);
    return () => clearTimeout(debouncedSearch);
  }, [searchTerm]);

  useEffect(() => {
    if (clinic?.id) {
      fetchPrescriptions();
    }
  }, [
    clinic?.id,
    pagination.page,
    pagination.search,
    sortField,
    sortOrder,
    statusFilter,
    paymentStatusFilter,
  ]);

  // Fetch status counts on initial load
  useEffect(() => {
    if (clinic?.id) {
      fetchStatusCounts();
    }
  }, [clinic?.id]);

  // Handle status filter from URL query parameter on mount
  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam === 'awaiting_payment') {
      setStatusFilter('');
      setPaymentStatusFilter('pending');
      setPagination(prev => ({ ...prev, page: 1 }));
    } else if (statusParam && statusParam !== statusFilter) {
      setStatusFilter(statusParam);
      setPaymentStatusFilter('all');
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [searchParams]);

  // Update URL when filter changes
  const handleStatusFilterChange = (value: string) => {
    if (value === 'awaiting_payment') {
      // Special tab: filter by payment_status = pending (unpaid), no prescription status filter
      setStatusFilter('');
      setPaymentStatusFilter('pending');
      searchParams.set('status', 'awaiting_payment');
    } else {
      setStatusFilter(value === 'all' ? '' : value);
      setPaymentStatusFilter('all');
      if (value === '' || value === 'all') {
        searchParams.delete('status');
      } else {
        searchParams.set('status', value);
      }
    }
    setPagination(prev => ({ ...prev, page: 1 }));
    setSearchParams(searchParams, { replace: true });
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Rotate colors for prescription icons
  const colors = [
    { bg: 'bg-teal-100', text: 'text-teal-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
  ];

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
            Order Management
          </h1>
          <p className='mt-1 text-sm text-slate-500'>
            Manage and track all orders for your clinic
          </p>
        </div>
        {!!clinic?.paymentAccount && !isGuided && permissionCheck('prescriptions:create', rolePermissions, isPractitioner || isAdmin) && (
          <div className='flex w-full items-center gap-3 sm:w-auto'>
            <Button
              onClick={handleNewPrescription}
              className='h-10 flex-1 gap-2 rounded-lg bg-brand-teal px-4 text-white hover:bg-brand-teal/90 sm:flex-none'
            >
              <Plus className='h-4 w-4' />
              Create Order
            </Button>
          </div>
        )}
      </div>

      {/* Payment Account Alert */}
      {!clinic?.paymentAccount && isAdmin && (
        <div className='relative w-full rounded-lg border border-amber-200 bg-amber-50 p-4'>
          <div className='flex items-center space-x-2'>
            <AlertTriangle className='h-4 w-4 text-amber-600' />
            <div className='flex-1 text-sm text-amber-800'>
              <div className='flex items-center justify-between'>
                <span>
                  Your clinic has not linked a payment account. Link a payment account to process
                  payments.
                </span>
              </div>
            </div>
            <Button
              size='sm'
              className='ml-4 bg-brand-teal hover:bg-brand-teal/90'
              onClick={() => navigate('/payments?tab=settings')}
            >
              Go to Payments
            </Button>
          </div>
        </div>
      )}

      {/* Status Tabs */}
      <Tabs
        value={paymentStatusFilter === 'pending' && !statusFilter ? 'awaiting_payment' : statusFilter || 'all'}
        onValueChange={value => handleStatusFilterChange(value)}
        className='w-full'
      >
        <div className='flex flex-col gap-4'>
          <div className='-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0'>
            <TabsList className='inline-flex h-10 w-auto min-w-full items-center justify-start rounded-lg bg-slate-100 p-1 sm:min-w-0'>
              <TabsTrigger
                value='all'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm'
              >
                All
                {statusCounts.all > 0 && (
                  <span className='rounded-full bg-slate-200 px-1.5 py-0.5 text-xs font-semibold text-slate-700 data-[state=active]:bg-slate-300'>
                    {statusCounts.all}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='pending'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-amber-600 data-[state=active]:shadow-sm'
              >
                <Clock className='h-3.5 w-3.5' />
                Pending Review
                {statusCounts.pending > 0 && (
                  <span className='rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700'>
                    {statusCounts.pending}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='verified'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-brand-teal data-[state=active]:shadow-sm'
              >
                <CheckCircle className='h-3.5 w-3.5' />
                Verified
                {statusCounts.verified > 0 && (
                  <span className='rounded-full bg-teal-100 px-1.5 py-0.5 text-xs font-semibold text-teal-700'>
                    {statusCounts.verified}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='awaiting_payment'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-rose-600 data-[state=active]:shadow-sm'
              >
                <CreditCard className='h-3.5 w-3.5' />
                Awaiting Payment
                {statusCounts.awaiting_payment > 0 && (
                  <span className='rounded-full bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700'>
                    {statusCounts.awaiting_payment}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='sent_to_pharmacy'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-brand-copper data-[state=active]:shadow-sm'
              >
                <Send className='h-3.5 w-3.5' />
                Sent to Pharmacy
                {statusCounts.sent_to_pharmacy > 0 && (
                  <span className='rounded-full bg-purple-100 px-1.5 py-0.5 text-xs font-semibold text-purple-700'>
                    {statusCounts.sent_to_pharmacy}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='acknowledged'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-indigo-500 data-[state=active]:shadow-sm'
              >
                <Check className='h-3.5 w-3.5' />
                Acknowledged
                {statusCounts.acknowledged > 0 && (
                  <span className='rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold text-indigo-700'>
                    {statusCounts.acknowledged}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='filled'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-orange-500 data-[state=active]:shadow-sm'
              >
                <Package className='h-3.5 w-3.5' />
                Being Filled
                {statusCounts.filled > 0 && (
                  <span className='rounded-full bg-orange-100 px-1.5 py-0.5 text-xs font-semibold text-orange-700'>
                    {statusCounts.filled}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='shipped'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-sky-500 data-[state=active]:shadow-sm'
              >
                <Truck className='h-3.5 w-3.5' />
                Shipped
                {statusCounts.shipped > 0 && (
                  <span className='rounded-full bg-sky-100 px-1.5 py-0.5 text-xs font-semibold text-sky-700'>
                    {statusCounts.shipped}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value='cancelled'
                className='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-red-500 data-[state=active]:shadow-sm'
              >
                <XCircle className='h-3.5 w-3.5' />
                Cancelled
                {statusCounts.cancelled > 0 && (
                  <span className='rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700'>
                    {statusCounts.cancelled}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Search and Payment Filter Row */}
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
            <div className='relative w-full sm:w-auto sm:min-w-[200px] md:min-w-[280px] lg:min-w-[380px]'>
              <Search className='absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400' />
              <Input
                placeholder='Search by patient, practitioner, medication, RX number...'
                className='w-full rounded-lg border-slate-200 bg-white pl-9'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value.trimStart())}
              />
            </div>

            <Select
              value={paymentStatusFilter}
              onValueChange={value => {
                setPaymentStatusFilter(value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className='ml-auto w-full sm:w-[140px]'>
                <SelectValue placeholder='Payment Status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='paid'>Paid</SelectItem>
                <SelectItem value='pending'>Unpaid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Tabs>

      {/* Filter Pills - Above Table */}
      {paymentStatusFilter !== 'all' && (
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-xs text-slate-500'>Filters:</span>
          <Badge
            variant='secondary'
            className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
          >
            Payment: {paymentStatusFilter === 'paid' ? 'Paid' : 'Unpaid'}
            <button
              onClick={() => {
                setPaymentStatusFilter('all');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className='ml-1.5 hover:text-slate-900'
            >
              <X className='h-3 w-3' />
            </button>
          </Badge>
          <button
            onClick={() => {
              setPaymentStatusFilter('all');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className='text-xs text-brand-teal hover:underline'
          >
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[900px] text-sm'>
            <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
              <tr className='border-b border-slate-200'>
                <th
                  className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  #
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Patient
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Medication
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Practitioner
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('created_at')}
                >
                  <div className='flex items-center gap-1'>
                    Date
                    <SortIcon field='created_at' />
                  </div>
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('status')}
                >
                  <div className='flex items-center gap-1'>
                    Status
                    <SortIcon field='status' />
                  </div>
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('priority')}
                >
                  <div className='flex items-center gap-1'>
                    Priority
                    <SortIcon field='priority' />
                  </div>
                </th>
                <th
                  className='sticky right-0 w-12 px-2 py-3'
                  style={{
                    background:
                      'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className='h-48 text-center'>
                    <div className='flex flex-col items-center justify-center text-slate-500'>
                      <Loader2 className='mb-3 h-8 w-8 animate-spin text-brand-teal' />
                      <p className='text-sm font-medium'>Loading orders...</p>
                    </div>
                  </td>
                </tr>
              ) : prescriptions.length === 0 ? (
                <tr>
                  <td colSpan={8} className='h-48 text-center'>
                    <div className='flex flex-col items-center justify-center text-slate-500'>
                      <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                        <FileText className='h-7 w-7 text-slate-400' />
                      </div>
                      <p className='font-medium text-slate-600'>
                        {searchTerm ? 'No orders found' : 'No orders yet'}
                      </p>
                      <p className='mt-1 text-sm text-slate-400'>
                        {searchTerm
                          ? 'Try adjusting your search or filters'
                          : 'Create your first order to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                prescriptions.map((prescription, index) => {
                  const color = colors[index % colors.length];
                  const email = getDefaultContactValue(prescription.patient?.contacts, 'email');
                  return (
                    <tr
                      key={prescription.id}
                      className='border-b border-slate-100 transition-colors hover:bg-slate-50/50'
                    >
                      <td
                        className='whitespace-nowrap px-4 py-3 text-slate-500'
                        style={{ fontSize: '0.8rem' }}
                      >
                        #{String((pagination.page - 1) * 10 + index + 1).padStart(3, '0')}
                      </td>
                      <td
                        className='max-w-[200px] whitespace-nowrap px-4 py-3'
                        style={{ fontSize: '0.8rem' }}
                      >
                        <div className='flex items-center gap-3'>
                          <div
                            className={`h-8 w-8 ${color.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
                          >
                            <FileText className={`h-4 w-4 ${color.text}`} />
                          </div>
                          <div className='min-w-0'>
                            <span className='block max-w-[150px] truncate font-medium capitalize text-slate-900'>
                              {prescription.patient?.full_name || '--'}
                            </span>
                            {prescription.is_refill_prescription && (
                              <Badge className='mt-0.5 border-0 bg-orange-500 px-1.5 py-0 text-[10px] text-white hover:bg-orange-500'>
                                Refill
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td
                        className='max-w-[220px] px-4 py-3 text-slate-600'
                        style={{ fontSize: '0.8rem' }}
                      >
                        {prescription.medicationRequests?.length
                          ? (() => {
                              const medications = prescription.medicationRequests;
                              const firstMed = medications[0];
                              const firstMedName = firstMed?.medication || '';
                              const firstRxNumber = firstMed?.rx_number;
                              const remainingCount = medications.length - 1;
                              const tooltipText = medications
                                .map(m =>
                                  m.rx_number
                                    ? `${m.medication} (RX: ${m.rx_number})`
                                    : m.medication
                                )
                                .join(', ');
                              return (
                                <span className='block truncate' title={tooltipText}>
                                  {firstMedName}
                                  {firstRxNumber && (
                                    <span className='ml-1 text-xs text-slate-400'>
                                      (RX: {firstRxNumber})
                                    </span>
                                  )}
                                  {remainingCount > 0 && (
                                    <span className='text-slate-400'>, +{remainingCount} more</span>
                                  )}
                                </span>
                              );
                            })()
                          : '--'}
                      </td>
                      <td
                        className='max-w-[150px] px-4 py-3 text-slate-600'
                        style={{ fontSize: '0.8rem' }}
                      >
                        <span
                          className='block truncate capitalize'
                          title={prescription.practitioner?.full_name || '--'}
                        >
                          {prescription.practitioner?.full_name || '--'}
                        </span>
                      </td>
                      <td
                        className='whitespace-nowrap px-4 py-3 text-slate-500'
                        style={{ fontSize: '0.8rem' }}
                      >
                        {formatToLocalDate(prescription.created_at, 'MM/DD/YYYY')}
                      </td>
                      <td className='whitespace-nowrap px-4 py-3'>
                        <div className='flex flex-col gap-1'>
                          {prescription.is_external_fulfillment && prescription.status !== 'pending' && prescription.status !== 'verified' ? (
                            <Badge className={`inline-flex w-fit items-center gap-1 text-xs ${
                              prescription.external_fulfillment_status === 'sent'
                                ? 'border border-teal-200 bg-teal-50 text-teal-700'
                                : prescription.external_fulfillment_status === 'shipped'
                                ? 'border border-sky-200 bg-sky-50 text-sky-700'
                                : prescription.external_fulfillment_status === 'delivered'
                                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border border-violet-200 bg-violet-50 text-violet-700'
                            }`}>
                              {prescription.external_fulfillment_status === 'sent'
                                ? 'Sent to External Pharmacy'
                                : prescription.external_fulfillment_status === 'shipped'
                                ? 'Shipped (External)'
                                : prescription.external_fulfillment_status === 'delivered'
                                ? 'Delivered (External)'
                                : 'Queued for External Fulfillment'}
                            </Badge>
                          ) : (
                            <Badge
                              className={`${getStatusColor(prescription.status)} inline-flex w-fit items-center gap-1 text-xs`}
                            >
                              {getStatusIcon(prescription.status)}
                              <span className='capitalize'>{getStatusText(prescription.status)}</span>
                            </Badge>
                          )}
                          {prescription.payment_status !== 'pending' && (
                            <Badge
                              className={`${getStatusColor(prescription.payment_status)} inline-flex w-fit items-center gap-1 text-xs`}
                            >
                              {getStatusIcon(prescription.payment_status)}
                              <span className='capitalize'>
                                {getStatusText(prescription.payment_status)}
                              </span>
                            </Badge>
                          )}
                          {/* Cancellation Rejection Status Indicator */}
                          {prescription.cancellationRequests?.some(
                            req => req.status === 'rejected'
                          ) && (
                            <Badge className='inline-flex w-fit items-center gap-1 border border-red-200 bg-red-50 text-xs text-red-700'>
                              <XCircle className='h-3 w-3' />
                              <span>Cancellation Rejected</span>
                            </Badge>
                          )}
                          {/* Pending Cancellation Request Indicator */}
                          {prescription.cancellationRequests?.some(
                            req => req.status === 'pending'
                          ) && (
                            <Badge className='inline-flex w-fit items-center gap-1 border border-amber-200 bg-amber-50 text-xs text-amber-700'>
                              <Clock className='h-3 w-3' />
                              <span>Cancellation Pending</span>
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className='whitespace-nowrap px-4 py-3'>
                        <Badge
                          variant='outline'
                          className={`${getPriorityColor(prescription.priority ?? 1)} inline-flex w-fit items-center gap-1 border text-xs`}
                        >
                          <Flag className='h-3 w-3' />
                          <span>{getPriorityText(prescription.priority ?? 1)}</span>
                        </Badge>
                      </td>
                      <td
                        className='sticky right-0 w-12 px-2 py-3'
                        style={{
                          background:
                            'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                        }}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant='ghost'
                              size='icon'
                              className='h-8 w-8 hover:bg-slate-100'
                            >
                              <MoreHorizontal className='h-4 w-4 text-slate-500' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end' className='w-52'>
                            <DropdownMenuItem
                              onClick={() => handleViewDetails(prescription)}
                              className='cursor-pointer'
                              style={{ fontSize: '13px' }}
                            >
                              <Eye className='mr-2 h-4 w-4' />
                              View Details
                            </DropdownMenuItem>

                            {(['pending', 'verified'].includes(prescription.status)) && prescription.payment_status !== 'paid' && isPractitioner && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedPrescription(prescription);
                                    setIsEditPrescriptionOpen(true);
                                  }}
                                  className='cursor-pointer text-slate-700'
                                  style={{ fontSize: '13px' }}
                                >
                                  <Pencil className='mr-2 h-4 w-4' />
                                  Edit Order
                                </DropdownMenuItem>
                              </>
                            )}

                            {prescription.status === 'pending' && isPractitioner && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedPrescription(prescription);
                                    setIsVerificationConfirmationOpen(true);
                                  }}
                                  className='cursor-pointer text-brand-teal'
                                  style={{ fontSize: '13px' }}
                                >
                                  <CheckCircle className='mr-2 h-4 w-4' />
                                  Verify
                                </DropdownMenuItem>
                              </>
                            )}

                            {prescription.status === 'verified' &&
                              prescription.payment_status === 'paid' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        const result = await prescriptionService.submitPrescription(
                                          prescription.id
                                        );
                                        fetchPrescriptions();
                                        toast({
                                          variant: 'success',
                                          title: (result as any).isExternalFulfillment ? 'Queued for external fulfillment' : 'Order submitted to pharmacy successfully',
                                        });
                                      } catch (error: any) {
                                        const errorMessage =
                                          error?.message ??
                                          error?.response?.data?.error?.message ??
                                          error?.response?.data?.message ??
                                          'Please try again later';
                                        toast({
                                          variant: 'destructive',
                                          title: 'Order submission failed',
                                          description: errorMessage,
                                        });
                                      }
                                    }}
                                    className='cursor-pointer text-brand-copper'
                                    style={{ fontSize: '13px' }}
                                  >
                                    <Send className='mr-2 h-4 w-4' />
                                    Submit to Pharmacy
                                  </DropdownMenuItem>
                                </>
                              )}

                            {(prescription.status === 'pending' ||
                              prescription.status === 'verified') &&
                              prescription.payment_status === 'pending' &&
                              !prescription.cancellationRequests?.some(
                                request => request.status === 'pending'
                              ) &&
                              permissionCheck(
                                'payments:collect_payment',
                                rolePermissions,
                                isPractitioner || isAdmin
                              ) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedPrescription(prescription);
                                      setIsPaymentSheetOpen(true);
                                    }}
                                    className='cursor-pointer text-emerald-600'
                                    style={{ fontSize: '13px' }}
                                  >
                                    <CreditCard className='mr-2 h-4 w-4' />
                                    Process Payment
                                  </DropdownMenuItem>
                                  {email && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setIsSendLinkSheetOpen({ open: true, prescription });
                                      }}
                                      className='cursor-pointer text-brand-copper'
                                      style={{ fontSize: '13px' }}
                                    >
                                      <Send className='mr-2 h-4 w-4' />
                                      Send Payment Link
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}

                            {/* Cancel Prescription Option - hide if cancellation request already exists or linked to guided order */}
                            {prescription.status !== 'shipped' &&
                              prescription.status !== 'delivered' &&
                              prescription.status !== 'cancelled' &&
                              (!prescription.cancellationRequests ||
                                prescription.cancellationRequests.length === 0) &&
                              !(prescription as any).guided_order_id &&
                              (isPractitioner || isAdmin) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedPrescription(prescription);
                                      setIsCancelDialogOpen(true);
                                    }}
                                    className='cursor-pointer text-red-600'
                                    style={{ fontSize: '13px' }}
                                  >
                                    <AlertCircle className='mr-2 h-4 w-4' />
                                    Cancel Prescription
                                  </DropdownMenuItem>
                                </>
                              )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && prescriptions.length > 0 && (
          <div className='flex flex-col items-center gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:justify-between'>
            <p className='whitespace-nowrap text-xs text-slate-500'>
              Showing {pagination.page! * pagination.itemsPerPage! - pagination.itemsPerPage! + 1} -{' '}
              {pagination.page! * pagination.itemsPerPage! > pagination.totalItems!
                ? pagination.totalItems
                : pagination.page! * pagination.itemsPerPage!}{' '}
              of {pagination.totalItems} patients
            </p>
            {pagination.totalPages && pagination.totalPages > 1 && (
              <SmartPagination
                currentPage={pagination.page ?? 1}
                onPageChange={page => setPagination(prev => ({ ...prev, page: page }))}
                totalPages={pagination.totalPages}
                maxVisiblePages={3}
                className='mx-0 justify-center sm:justify-end'
              />
            )}
          </div>
        )}
      </div>

      {/* Prescription Detail Sheet */}
      {selectedPrescription && (
        <PrescriptionDetailSheet
          open={isDetailSheetOpen}
          onOpenChange={setIsDetailSheetOpen}
          prescription={selectedPrescription}
          onStatusUpdate={handleStatusUpdate}
          onActionComplete={() => {
            fetchPrescriptions();
            setIsDetailSheetOpen(false);
          }}
        />
      )}

      {/* Payment Confirmation Dialog */}
      {selectedPrescription && (
        <PaymentConfirmationDialog
          open={isPaymentConfirmationOpen}
          onOpenChange={setIsPaymentConfirmationOpen}
          prescriptionId={selectedPrescription.id}
          patientName={selectedPrescription.patient?.full_name}
          onConfirm={async () => {
            try {
              setIsPaymentProcessing(true);
              const result = await prescriptionService.submitPrescription(selectedPrescription.id);
              fetchPrescriptions();
              toast({
                title: 'Success',
                description: (result as any).isExternalFulfillment ? 'Payment processed — prescription queued for external fulfillment' : 'Payment processed and order placed successfully',
                variant: 'success',
              });
            } catch (error: any) {
              console.error('Error processing payment:', error);
              toast({
                title: 'Error',
                description: error.message || 'Failed to process payment',
                variant: 'destructive',
              });
              throw error;
            } finally {
              setIsPaymentProcessing(false);
            }
          }}
          isLoading={isPaymentProcessing}
        />
      )}

      {/* Verification Confirmation Dialog */}
      {selectedPrescription && (
        <VerificationConfirmationDialog
          open={isVerificationConfirmationOpen}
          onOpenChange={setIsVerificationConfirmationOpen}
          prescriptionId={selectedPrescription.id}
          patientName={selectedPrescription.patient?.full_name}
          practitionerName={selectedPrescription.practitioner?.full_name}
          onConfirm={async () => {
            try {
              setIsVerificationProcessing(true);
              await prescriptionService.verifyPrescription(selectedPrescription.id);
              if (selectedPrescription.payment_status === 'paid') {
                await prescriptionService.submitPrescription(selectedPrescription.id);
              }
              fetchPrescriptions();
              toast({
                title: 'Success',
                description: 'Order verified successfully',
                variant: 'success',
              });
            } catch (error: any) {
              console.error('Error verifying prescription:', error);
              toast({
                title: 'Error',
                description: error.message || 'Failed to verify order',
                variant: 'destructive',
              });
              throw error;
            } finally {
              setIsVerificationProcessing(false);
            }
          }}
          isLoading={isVerificationProcessing}
        />
      )}

      {/* Payment Sheet */}
      {selectedPrescription && isPaymentSheetOpen && (
        <PaymentSheet
          open={isPaymentSheetOpen}
          onOpenChange={setIsPaymentSheetOpen}
          prescriptionId={selectedPrescription.id}
          onPaymentComplete={async () => {
            try {
              setIsPaymentProcessing(true);
              if (selectedPrescription.status === 'verified') {
                await prescriptionService.submitPrescription(selectedPrescription.id);
              }
              fetchPrescriptions();
              toast({
                title: 'Success',
                description: 'Payment processed and order placed successfully',
                variant: 'success',
              });
              setIsPaymentSheetOpen(false);
            } catch (error: any) {
              console.error('Error processing payment:', error);
              toast({
                title: 'Error',
                description: error.message || 'Failed to process payment',
                variant: 'destructive',
              });
              throw error;
            } finally {
              setIsPaymentProcessing(false);
            }
          }}
          onPrescriptionUpdated={() => {
            fetchPrescriptions(); // Refetch list when medications are removed
          }}
        />
      )}

      {/* Create Prescription Sheet */}
      {isSheetOpen && (
        <PrescriptionFormSheet
          open={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          patientId={null}
          practitionerId={user?.practitionerId}
          mode='create'
          onSubmit={handleCreationSuccess}
        />
      )}

      {/* Send Link Dialog */}
      {isSendLinkSheetOpen.open && isSendLinkSheetOpen.prescription && (
        <SendPaymentLinkDialog
          open={isSendLinkSheetOpen.open}
          onOpenChange={() => setIsSendLinkSheetOpen({ open: false, prescription: null })}
          prescription_id={isSendLinkSheetOpen.prescription.id}
          onSuccess={() => {
            setIsSendLinkSheetOpen({ open: false, prescription: null });
          }}
          onPrescriptionUpdated={() => {
            fetchPrescriptions(); // Refetch list when medications are removed
          }}
        />
      )}

      {/* Cancel Prescription Dialog */}
      {selectedPrescription && (
        <CancelPrescriptionDialog
          open={isCancelDialogOpen}
          onOpenChange={setIsCancelDialogOpen}
          prescription={selectedPrescription}
          onSuccess={() => {
            fetchPrescriptions();
            setIsCancelDialogOpen(false);
          }}
        />
      )}

      {/* Edit Medications Dialog */}
      {selectedPrescription && (
        <EditMedicationsDialog
          open={isEditMedicationsOpen}
          onOpenChange={setIsEditMedicationsOpen}
          prescription={selectedPrescription}
          onMedicationsUpdated={() => {
            fetchPrescriptions();
          }}
        />
      )}

      {/* Edit Prescription */}
      {selectedPrescription && (
        <PrescriptionFormSheet
          open={isEditPrescriptionOpen}
          onOpenChange={setIsEditPrescriptionOpen}
          mode="edit"
          initialData={selectedPrescription}
          patientId={selectedPrescription.patient?.id || ''}
          practitionerId={selectedPrescription.practitioner?.id || ''}
          onSubmit={() => {
            fetchPrescriptions();
          }}
        />
      )}
    </div>
  );
};

export default ClinicPrescriptions;
